// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorOrderDetailPage
// =============================================================================

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, CheckCircle, XCircle, Package, Loader2 } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { OrderStatusBadge } from '@/features/vendor/components/OrderStatusBadge';
import {
  useVendorOrder,
  useConfirmOrder,
  useShipOrder,
  useDeliverOrder,
  useCancelOrder,
} from '@/features/vendor/hooks/useVendorOrders';

export function VendorOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading } = useVendorOrder(id || '');
  const confirmOrder = useConfirmOrder();
  const shipOrder = useShipOrder();
  const deliverOrder = useDeliverOrder();
  const cancelOrder = useCancelOrder();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-warm-gray">Order not found</p>
      </div>
    );
  }

  const handleConfirm = async () => {
    await confirmOrder.mutateAsync(order.id);
  };

  const handleShip = async () => {
    const trackingNumber = prompt('Enter tracking number:');
    if (trackingNumber) {
      await shipOrder.mutateAsync({ orderId: order.id, trackingNumber });
    }
  };

  const handleDeliver = async () => {
    await deliverOrder.mutateAsync(order.id);
  };

  const handleCancel = async () => {
    const reason = prompt('Reason for cancellation:');
    if (reason) {
      await cancelOrder.mutateAsync({ orderId: order.id, reason });
    }
  };

  return (
    <div className="min-h-screen">
      <VendorHeader
        title={`Order ${order.orderNumber}`}
        subtitle={`Placed on ${formatDate(order.orderedAt)}`}
        actions={
          <button
            onClick={() => navigate('/vendor/orders')}
            className="flex items-center gap-2 px-4 py-2 text-navy font-medium hover:bg-cream-dark rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Orders
          </button>
        }
      />

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status & Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-lg font-semibold text-navy mb-2">
                    Order Status
                  </h2>
                  <OrderStatusBadge status={order.status} />
                </div>
                <div className="flex gap-2">
                  {order.status === 'pending' && (
                    <>
                      <button
                        onClick={handleConfirm}
                        disabled={confirmOrder.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Confirm Order
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={cancelOrder.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel
                      </button>
                    </>
                  )}
                  {(order.status === 'confirmed' || order.status === 'processing') && (
                    <button
                      onClick={handleShip}
                      disabled={shipOrder.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Truck className="w-4 h-4" />
                      Mark Shipped
                    </button>
                  )}
                  {order.status === 'shipped' && (
                    <button
                      onClick={handleDeliver}
                      disabled={deliverOrder.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Package className="w-4 h-4" />
                      Mark Delivered
                    </button>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="border-t border-gray-100 pt-6">
                <h3 className="font-semibold text-navy mb-4">Order Timeline</h3>
                <div className="space-y-4">
                  <TimelineItem
                    label="Order Placed"
                    date={order.orderedAt}
                    completed
                  />
                  <TimelineItem
                    label="Confirmed"
                    date={order.confirmedAt}
                    completed={!!order.confirmedAt}
                  />
                  <TimelineItem
                    label="Shipped"
                    date={order.shippedAt}
                    completed={!!order.shippedAt}
                  />
                  <TimelineItem
                    label="Delivered"
                    date={order.deliveredAt}
                    completed={!!order.deliveredAt}
                  />
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-display text-lg font-semibold text-navy mb-4">
                Product
              </h2>
              <div className="flex gap-4">
                {order.productImage && (
                  <img
                    src={order.productImage}
                    alt={order.productName}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                )}
                <div>
                  <h3 className="font-semibold text-navy">{order.productName}</h3>
                  <p className="text-sm text-warm-gray mt-1">{order.description}</p>
                  <p className="text-sm text-navy mt-2">Quantity: {order.quantity}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {(order.customerNotes || order.vendorNotes) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-display text-lg font-semibold text-navy mb-4">
                  Notes
                </h2>
                {order.customerNotes && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-warm-gray mb-1">
                      Customer Notes
                    </h3>
                    <p className="text-navy">{order.customerNotes}</p>
                  </div>
                )}
                {order.vendorNotes && (
                  <div>
                    <h3 className="text-sm font-medium text-warm-gray mb-1">
                      Your Notes
                    </h3>
                    <p className="text-navy">{order.vendorNotes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-display text-lg font-semibold text-navy mb-4">
                Customer
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-warm-gray">Name</p>
                  <p className="font-medium text-navy">{order.customer.contactName}</p>
                </div>
                <div>
                  <p className="text-sm text-warm-gray">Email</p>
                  <p className="font-medium text-navy">{order.customer.email}</p>
                </div>
                {order.customer.phone && (
                  <div>
                    <p className="text-sm text-warm-gray">Phone</p>
                    <p className="font-medium text-navy">{order.customer.phone}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-display text-lg font-semibold text-navy mb-4">
                Shipping Address
              </h2>
              <address className="not-italic text-navy">
                {order.shippingAddress.street}<br />
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
              </address>
              {order.trackingNumber && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-warm-gray">Tracking Number</p>
                  <p className="font-medium text-navy">{order.trackingNumber}</p>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-display text-lg font-semibold text-navy mb-4">
                Order Summary
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-warm-gray">Subtotal</span>
                  <span className="text-navy">{formatCurrency(order.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-warm-gray">Shipping</span>
                  <span className="text-navy">{formatCurrency(order.shipping)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-warm-gray">Tax</span>
                  <span className="text-navy">{formatCurrency(order.tax)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="font-semibold text-navy">Total</span>
                  <span className="font-semibold text-navy">{formatCurrency(order.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({
  label,
  date,
  completed,
}: {
  label: string;
  date?: string | null;
  completed: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-3 h-3 rounded-full ${
          completed ? 'bg-green-500' : 'bg-gray-300'
        }`}
      />
      <div className="flex-1">
        <span className={completed ? 'text-navy' : 'text-warm-gray'}>{label}</span>
      </div>
      {date && (
        <span className="text-sm text-warm-gray">
          {new Date(date).toLocaleDateString()}
        </span>
      )}
    </div>
  );
}

export default VendorOrderDetailPage;
